import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Menu,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { surveyApi } from '../../config/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import AccountingDataTable from '../../components/accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../../components/accounting/shared/AccountingDataTable';

interface QRMenuFeedbackItem {
  Id: number;
  CompanyId: string;
  MenuId: string;
  BranchId?: string | null;
  BranchName?: string | null;
  Name?: string | null;
  Surname?: string | null;
  Phone?: string | null;
  Email?: string | null;
  Subject?: string | null;
  Message: string;
  IpAddress?: string | null;
  UserAgent?: string | null;
  CreatedAt: string;
  IsRead: boolean;
  ReadAt?: string | null;
  ReadBy?: string | null;
  Notes?: string | null;
  UpdatedAt?: string | null;
}

interface ListResponse {
  Success: boolean;
  Data: QRMenuFeedbackItem[];
  Page: number;
  PageSize: number;
  TotalCount: number;
  UnreadCount: number;
}

const READ_FILTER_OPTIONS = [
  { value: 'all', label: 'Tümü' },
  { value: 'unread', label: 'Sadece Okunmamış' },
  { value: 'read', label: 'Sadece Okunmuş' },
];

const formatDateTime = (raw?: string | null): string => {
  if (!raw) return '-';
  const hasTimeZoneInfo = /([zZ]|[+\-]\d{2}:\d{2})$/.test(raw);
  const normalizedRaw = hasTimeZoneInfo ? raw : `${raw}Z`;
  const d = new Date(normalizedRaw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const QRMenuFeedbackList: React.FC = () => {
  const { currentUser } = useAuth();

  const [items, setItems] = useState<QRMenuFeedbackItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [readFilter, setReadFilter] = useState<'all' | 'read' | 'unread'>('all');

  const [selected, setSelected] = useState<QRMenuFeedbackItem | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [actionMenuItem, setActionMenuItem] = useState<QRMenuFeedbackItem | null>(null);

  const readByLabel = useMemo(() => {
    return currentUser?.email || currentUser?.displayName || 'gts-web user';
  }, [currentUser]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        page: String(page + 1),
        pageSize: String(rowsPerPage),
      };
      if (search.trim()) params.search = search.trim();
      if (readFilter === 'read') params.isRead = 'true';
      if (readFilter === 'unread') params.isRead = 'false';

      const { data } = await surveyApi.get<ListResponse>('/api/QRMenuFeedback', { params });
      setItems(data?.Data || []);
      setTotalCount(data?.TotalCount || 0);
      setUnreadCount(data?.UnreadCount || 0);
    } catch (err: any) {
      const msg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        err?.message ||
        'Geri bildirimler yüklenemedi.';
      setError(msg);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, readFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleDataTablePageChange = (newPage: number, newPageSize: number) => {
    if (newPageSize !== rowsPerPage) {
      setRowsPerPage(newPageSize);
      setPage(0);
      return;
    }
    setPage(newPage);
  };

  const handleToggleRead = async (item: QRMenuFeedbackItem) => {
    setBusyId(item.Id);
    try {
      await surveyApi.post('/api/QRMenuFeedback/update', {
        Id: item.Id,
        IsRead: !item.IsRead,
        ReadBy: !item.IsRead ? readByLabel : null,
      });
      await fetchItems();
    } catch (err: any) {
      const msg =
        err?.response?.data?.Message ||
        err?.message ||
        'Güncelleme başarısız.';
      setError(msg);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item: QRMenuFeedbackItem) => {
    const confirmed = window.confirm(
      `Bu geri bildirimi silmek istediğinize emin misiniz?\n\nMesaj: ${item.Message.substring(0, 80)}${item.Message.length > 80 ? '...' : ''}`,
    );
    if (!confirmed) return;

    setBusyId(item.Id);
    try {
      await surveyApi.post('/api/QRMenuFeedback/delete', { Id: item.Id });
      if (selected?.Id === item.Id) setSelected(null);
      await fetchItems();
    } catch (err: any) {
      const msg =
        err?.response?.data?.Message ||
        err?.message ||
        'Silme başarısız.';
      setError(msg);
    } finally {
      setBusyId(null);
    }
  };

  const openDetail = (item: QRMenuFeedbackItem) => {
    setSelected(item);
    if (!item.IsRead) {
      handleToggleRead(item);
    }
  };

  const openActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    item: QRMenuFeedbackItem,
  ) => {
    event.stopPropagation();
    setActionMenuAnchorEl(event.currentTarget);
    setActionMenuItem(item);
  };

  const closeActionMenu = () => {
    setActionMenuAnchorEl(null);
    setActionMenuItem(null);
  };

  const handleActionMenuDetail = () => {
    if (!actionMenuItem) return;
    const targetItem = actionMenuItem;
    closeActionMenu();
    openDetail(targetItem);
  };

  const handleActionMenuToggleRead = () => {
    if (!actionMenuItem) return;
    const targetItem = actionMenuItem;
    closeActionMenu();
    handleToggleRead(targetItem);
  };

  const handleActionMenuDelete = () => {
    if (!actionMenuItem) return;
    const targetItem = actionMenuItem;
    closeActionMenu();
    handleDelete(targetItem);
  };

  const dataTableColumns = useMemo<DataTableColumn<QRMenuFeedbackItem>[]>(() => {
    return [
      {
        id: 'createdAt',
        label: 'Tarih',
        width: '130px',
        render: (row) => formatDateTime(row.CreatedAt),
      },
      {
        id: 'status',
        label: 'Durum',
        width: '90px',
        render: (row) => (
          <Chip
            size="small"
            label={row.IsRead ? 'Okundu' : 'Yeni'}
            color={row.IsRead ? 'default' : 'warning'}
            variant={row.IsRead ? 'outlined' : 'filled'}
          />
        ),
      },
      {
        id: 'fullName',
        label: 'Ad Soyad',
        minWidth: '130px',
        render: (row) => [row.Name, row.Surname].filter(Boolean).join(' ').trim() || '-',
      },
      {
        id: 'contact',
        label: 'İletişim',
        minWidth: '220px',
        render: (row) => [row.Phone, row.Email].filter(Boolean).join(' / ').trim() || '-',
      },
      {
        id: 'subject',
        label: 'Konu',
        minWidth: '120px',
        render: (row) => row.Subject || '-',
      },
      {
        id: 'message',
        label: 'Mesaj',
        minWidth: '220px',
        render: (row) =>
          row.Message.length > 80 ? `${row.Message.substring(0, 80)}…` : row.Message,
      },
      {
        id: 'branch',
        label: 'Şube',
        minWidth: '120px',
        render: (row) => row.BranchName || row.BranchId || '-',
      },
      {
        id: 'actions',
        label: 'İşlemler',
        align: 'right',
        width: '90px',
        render: (row) => (
          <Tooltip title="İşlemler">
            <span>
              <IconButton
                size="small"
                disabled={busyId === row.Id}
                onClick={(e) => openActionMenu(e, row)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ];
  }, [busyId]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          backgroundColor: 'background.paper',
          p: 2,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        }}
      >
        <Box>
          <Typography variant="h5" component="h2" fontWeight="bold" color="primary">
            QR Menü Geri Bildirimleri
          </Typography>
          <Typography variant="caption" color="text.secondary">
            QR menü üzerinden gelen tüm müşteri geri bildirimleri
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip
            label={`Toplam: ${totalCount}`}
            color="primary"
            variant="outlined"
            size="small"
          />
          <Chip
            label={`Okunmamış: ${unreadCount}`}
            color={unreadCount > 0 ? 'warning' : 'default'}
            variant={unreadCount > 0 ? 'filled' : 'outlined'}
            size="small"
          />
          <Tooltip title="Yenile">
            <span>
              <IconButton onClick={fetchItems} disabled={loading} color="primary">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Ara"
            placeholder="Ad, soyad, e-mail, mesaj..."
            value={search}
            onChange={(e) => {
              setPage(0);
              setSearch(e.target.value);
            }}
            size="small"
            sx={{ minWidth: 280, flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              label="Durum"
              value={readFilter}
              onChange={(e) => {
                setPage(0);
                setReadFilter(e.target.value as 'all' | 'read' | 'unread');
              }}
            >
              {READ_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <AccountingDataTable
        columns={dataTableColumns}
        rows={items}
        getRowId={(row) => String(row.Id)}
        loading={loading}
        onRowClick={openDetail}
        clickable
        emptyMessage="Bu kritere uyan geri bildirim bulunamadı."
        serverSidePagination
        totalCount={totalCount}
        page={page}
        pageSize={rowsPerPage}
        onPageChange={handleDataTablePageChange}
        rowsPerPageOptions={[10, 20, 50, 100]}
        getRowSx={(row) => ({
          backgroundColor: !row.IsRead ? 'rgba(255, 193, 7, 0.08)' : undefined,
        })}
      />
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl) && Boolean(actionMenuItem)}
        onClose={closeActionMenu}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleActionMenuDetail}>
          <VisibilityIcon fontSize="small" style={{ marginRight: 8 }} />
          Detay
        </MenuItem>
        <MenuItem onClick={handleActionMenuToggleRead}>
          {actionMenuItem?.IsRead ? (
            <MarkEmailUnreadIcon fontSize="small" style={{ marginRight: 8 }} />
          ) : (
            <MarkEmailReadIcon fontSize="small" style={{ marginRight: 8 }} />
          )}
          {actionMenuItem?.IsRead ? 'Okunmadı işaretle' : 'Okundu işaretle'}
        </MenuItem>
        <MenuItem onClick={handleActionMenuDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Sil
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Geri Bildirim Detayı</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">Tarih</Typography>
                <Typography variant="body2">{formatDateTime(selected.CreatedAt)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Ad Soyad</Typography>
                <Typography variant="body2">
                  {[selected.Name, selected.Surname].filter(Boolean).join(' ').trim() || '-'}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Telefon</Typography>
                  <Typography variant="body2">{selected.Phone || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">E-mail</Typography>
                  <Typography variant="body2">{selected.Email || '-'}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Konu</Typography>
                <Typography variant="body2">{selected.Subject || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Mesaj</Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', backgroundColor: 'grey.50', p: 1.5, borderRadius: 1 }}
                >
                  {selected.Message}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Şube</Typography>
                  <Typography variant="body2">{selected.BranchName || selected.BranchId || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Menü ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {selected.MenuId}
                  </Typography>
                </Box>
              </Box>
              {selected.IsRead && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Okundu Bilgisi</Typography>
                  <Typography variant="body2">
                    {formatDateTime(selected.ReadAt)} {selected.ReadBy ? `· ${selected.ReadBy}` : ''}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Kapat</Button>
          {selected && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => handleToggleRead(selected)}
              disabled={busyId === selected.Id}
            >
              {selected.IsRead ? 'Okunmadı İşaretle' : 'Okundu İşaretle'}
            </Button>
          )}
          {selected && (
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(selected)}
              disabled={busyId === selected.Id}
            >
              Sil
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QRMenuFeedbackList;
